import type { Translations } from './types';

export const en: Translations = {
  common: {
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    retry: 'Retry',
    loading: 'Loading...',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    or: 'or',
    play: 'Play',
    stop: 'Stop',
    goBack: 'Go back',
    returnHome: 'Return Home',
    dismiss: 'Dismiss',
  },
  login: {
    appName: 'CARO Care',
    subtitle: 'Sign in to continue',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    signingIn: 'Signing in...',
    signIn: 'Sign In',
    qrButton: 'Activate with QR Code',
    helpText: 'Need help? Contact',
    adminSetup: 'Admin Device Setup',
    activeSessionTitle: 'Another device is logged in',
    activeSessionMessage: 'This account is active on another device. Continuing will log out all other devices.',
    logoutOthers: 'Continue & Logout Others',
    cancelLogin: 'Cancel',
  },
  home: {
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    greetingNight: 'Good night',
    phoneReady: 'Ready',
    phoneConnecting: '...',
    phoneError: 'Err',
    geofenceOutside: 'Outside geofence!',
    geofenceInside: 'Inside geofence',
    geofenceIndicatorOutside: 'Outside',
    geofenceIndicatorSafe: 'Safe',
    editLayoutEdit: 'Edit home screen layout',
    editLayoutDone: 'Done editing layout',
    openDoor: 'Open door',
    openDoorSuccess: 'Door opening',
    openDoorError: 'Could not open the door',
    apps: 'Apps',
    widgetButtons: 'Buttons',
  },
  emergency: {
    confirmationTitle: 'Are you sure you need emergency help?',
    buttonYes: 'YES',
    buttonNo: 'NO',
    buttonNoCancel: 'No, cancel',
    buttonCancel: 'CANCEL',
    countdownMessage: 'Emergency call in {seconds} seconds...',
    calling: 'Calling emergency services...',
    connected: 'Connected',
    resetAlarm: 'Reset Alarm',
    cancelled: 'Emergency cancelled',
    errorApiFailed: 'Could not reach emergency services via app.',
    errorSipUnavailable: 'Phone service is unavailable. For emergencies, dial 112 directly.',
    call112: 'Call 112',
    tryAgain: 'Try Again',
    callHelp: 'Call help',
    fiveSecondsRemaining: '5 seconds remaining',
    dispatchConnecting: 'Alerting the care team…',
    dispatchRetry: 'Trying again… (attempt {n})',
    dispatchFailed: 'Could not connect. We keep trying…',
    dispatchFailedVoice: 'We could not reach the care team yet. We keep trying. Please stay calm.',
    tryingToCall: 'Calling emergency services…',
    callingWith: 'Connected with emergency services',
    onRoute: 'Emergency service is on the way',
    alarmSent: 'Alarm sent, help is being notified…',
    alarmActive: 'Help is being arranged. Please stay calm.',
  },
  settings: {
    title: 'Settings',
    homeScreen: 'Home Screen',
    customizeButtons: 'Customize buttons',
    connectionStatus: 'Connection Status',
    allConnected: 'All connected',
    display: 'Display & Theme',
    language: 'Language',
    about: 'About CARO Care',
    testMode: 'Test Mode',
    logout: 'Log Out',
    replayOnboarding: 'Replay Welcome Tour',
    permissions: 'App Permissions',
    permissionsValue: 'Microphone, camera, location',
    permissionsTitle: 'App Permissions',
    permissionsIntro: 'These permissions let the device use calls, video and notifications properly.',
    permissionsAllowAll: 'Allow all permissions',
    permissionsRecheck: 'Re-check',
    permissionsAllGranted: 'All permissions are granted',
    permGranted: 'Granted',
    permDenied: 'Denied',
    permPrompt: 'Not yet allowed',
    permDeniedHelp: 'Denied. Enable it from the device system settings.',
    permMic: 'Microphone',
    permMicDesc: 'Needed for voice calls',
    permCamera: 'Camera',
    permCameraDesc: 'Needed for video calls and the door camera',
    permLocation: 'Location',
    permLocationDesc: 'Needed for safety and geofencing',
    permNotifications: 'Notifications',
    permNotificationsDesc: 'Needed to show incoming calls',
  },
  buttonLabels: {
    dialer: 'Call',
    contacts: 'Contacts',
    photos: 'Photos',
    info: 'Info',
    games: 'Games',
    voicemail: 'Voicemail',
    callHistory: 'Call History',
    settings: 'Settings',
    openDoor: 'Open door',
    agenda: 'Today',
    weather: 'Weather',
    news: 'News',
  },
  onboarding: {
    splashTagline: 'Care at your fingertips',
    welcomeTitle: 'Welcome to CARO Care',
    welcomeSubtitle: 'Your personal care companion, designed to keep you connected and safe.',
    connectedTitle: 'Stay Connected',
    connectedSubtitle: 'Make calls, see who is at the door, and stay in touch with family and caregivers.',
    homeScreenTitle: 'Your Home Screen',
    homeScreenSubtitle: 'Weather, news, your schedule, and more — all customizable to your preferences.',
    getStartedTitle: 'Ready to Get Started',
    getStartedSubtitle: 'Let us set up your device. It only takes a moment.',
    next: 'Next',
    skip: 'Skip',
    getStartedButton: 'Get Started',
  },
  contacts: {
    title: 'Contacts',
    emptyTitle: 'No contacts yet.',
    emptyDescription: 'Your family and caregivers can add contacts for you through the portal.',
    callContact: 'Call',
    loadError: 'Could not load contacts. Please try again.',
  },
  callHistory: {
    title: 'Call History',
    filterAll: 'All',
    filterMissed: 'Missed',
    filterAnswered: 'Answered',
    filterVoicemail: 'Voicemail',
    loadError: 'Failed to load call history',
    networkError: 'Network error',
    noCalls: 'No calls yet',
    today: 'Today',
    yesterday: 'Yesterday',
    statusAnswered: 'Answered',
    statusMissed: 'Missed',
    statusVoicemail: 'Voicemail',
    statusRinging: 'Ringing',
    statusRejected: 'Rejected',
    statusFailed: 'Failed',
  },
  voicemail: {
    title: 'Voicemail',
    loadingVoicemails: 'Loading voicemails...',
    loadError: 'Failed to load voicemails',
    emptyTitle: 'No voicemails',
    emptyDescription: 'When someone leaves you a voicemail, it will appear here.',
    statusKept: 'KEPT',
    statusAutoDelete: '14d auto-delete',
    keep: 'Keep',
    deleteVoicemail: 'Delete',
    confirmDelete: 'Delete this voicemail?',
    playError: 'Failed to play voicemail',
    deleteError: 'Failed to delete voicemail',
    updateError: 'Failed to update voicemail',
    daysAgo: '{n} days ago',
    today: 'Today',
    yesterday: 'Yesterday',
  },
  inCall: {
    connecting: 'Connecting...',
    dialing: 'Calling...',
    onHold: 'On Hold',
    ended: 'Call Ended',
    mute: 'Mute',
    unmute: 'Unmute',
    keypad: 'Keypad',
    speaker: 'Speaker',
    earpiece: 'Earpiece',
    hold: 'Hold',
    resume: 'Resume',
  },
  incomingCall: {
    emergencyCallback: 'Emergency Callback',
    incoming: 'Incoming Call',
    autoAnswering: 'Auto-answering...',
    decline: 'Decline',
    answer: 'Answer',
  },
  doorbell: {
    viewing: 'Someone is at the door',
    calling: 'Connecting...',
    active: 'Connected',
    doorOpened: 'Door opened',
    opening: 'Opening door...',
    done: 'Call ended',
    noCamera: 'No camera available',
    entrance: 'Entrance',
    openDoor: 'Open Door',
    answer: 'Answer',
    decline: 'Decline',
    hangup: 'End call',
    afwijzen: 'Reject',
    opnemen: 'Answer',
    ophangen: 'Hang up',
    deurOpenen: 'Open door',
    // See nl.ts for why these four exist and why `doorOpened` is not reused.
    doorReleasing: 'Opening the door',
    doorRetried: 'Tried again',
    doorSent: 'Door command sent',
    doorFailed: 'Could not open',
  },
  photos: {
    title: 'Photos',
    tabAll: 'All',
    tabFavorites: 'Favorites',
    tabHidden: 'Hidden',
    emptyFavorites: 'No favorite photos yet. Tap the heart on a photo to add it!',
    emptyHidden: 'No hidden photos.',
    emptyAll: 'No photos yet. Your family can send photos through their link.',
    unavailable: 'Photo unavailable',
  },
  agenda: {
    title: 'Schedule',
    today: 'Today',
    allDay: 'All day',
    spots: 'spots',
    open: 'Open',
    addedBy: 'Added by',
    emptyMessage: 'No events scheduled for this week',
    cancelSignup: 'Cancel Signup',
    join: 'Join',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    tomorrow: 'Tomorrow',
    addEntry: '+ Add',
    privateMarker: 'Private',
    privateExplain: 'Only you can see this',
    newEntryTitle: 'Put something in your agenda',
    entryTitleLabel: 'What is it?',
    entryWhenLabel: 'When?',
    entryDurationLabel: 'How long?',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    signupOpen: 'Sign up',
    signupClosed: 'Signups are closed',
    signupFull: 'Full',
    signupAlready: 'You are signed up',
    moreInfo: 'More info',
    withdrawClosed: 'This activity has started or is over. You can no longer withdraw — please ask a member of staff.',
    withdrawFailed: 'Could not withdraw',
    noBillingAccount: 'Sign up through the staff',
    waitlisted: 'You are on the waiting list',
    whoIsComing: 'Who is coming?',
    attendeeYou: 'You',
    confirmTitle: 'Just to confirm',
    confirmTotal: 'Total',
    confirmOnApartmentBill: "This goes on your apartment's bill.",
    confirmYes: 'Yes, sign me up',
    confirmNo: 'No, never mind',
    signupFailed: 'Signing up did not work. Try again or ask the staff.',
    priceChanged: 'The price has changed in the meantime. Please look again.',
  },
  dialer: {
    title: 'Dialer',
    connectingMessage: 'Connecting to phone service...',
    notConnectedMessage: 'Phone service not connected',
  },
  info: {
    title: 'Info',
    myProfile: 'My Profile',
    name: 'Name',
    email: 'Email',
    location: 'Location',
    myBuilding: 'My Building',
    building: 'Building',
    needHelp: 'Need Help?',
    helpText: 'Press the red emergency button on the home screen or ask a caregiver for assistance.',
    about: 'About this app',
    appVersion: 'Version',
  },
  connectivity: {
    title: 'Connection Status',
    allConnected: 'All Systems Connected',
    limited: 'Limited Connectivity',
    offline: 'Offline',
    checking: 'Checking...',
    emergencyAvailable: 'Emergency calls available',
    degradedMessage: 'Some services may be unavailable',
    offlineMessage: 'No connection detected',
    networkLabel: 'Wi-Fi / Network',
    networkDescription: 'Local network connection',
    internetLabel: 'Internet',
    internetDescription: 'Internet access',
    apiLabel: 'CARO Server',
    apiDescription: 'CARO cloud services',
    sipLabel: 'Phone Service',
    sipDescription: 'Voice call capability',
    mqttLabel: 'Monitoring',
    mqttDescription: 'Device telemetry & remote commands',
    statusOk: 'OK',
    statusDown: 'Down',
    checkAgain: 'Check Again',
    noNetwork: 'No network connection',
    offlineEmergencyText: 'For emergencies, dial 112 directly using your phone.',
  },
  homeScreenSettings: {
    title: 'Home Screen',
    subtitle: 'Choose which buttons appear on your home page',
    buttonCount: '{count} of {total} buttons visible',
    resetToDefault: 'Reset to Default',
  },
  games: {
    title: 'Games',
    subtitle: 'Keep your mind sharp and have fun',
    memory: { name: 'Memory', desc: 'Match the pairs' },
    sudoku: { name: 'Sudoku', desc: 'Number puzzle' },
    wordSearch: { name: 'Word Search', desc: 'Find hidden words' },
    trivia: { name: 'Trivia', desc: 'Test your knowledge' },
    game2048: { name: '2048', desc: 'Slide & merge tiles' },
    solitaire: { name: 'Solitaire', desc: 'Classic card game' },
    ticTacToe: { name: 'Tic-Tac-Toe', desc: 'X and O strategy' },
    hangman: { name: 'Hangman', desc: 'Guess the word' },
    colorMatch: { name: 'Color Match', desc: 'Remember the pattern' },
    chess: { name: 'Chess', desc: 'Classic strategy game' },
    checkers: { name: 'Checkers', desc: 'Jump and capture' },
    findDifferences: { name: 'Find Differences', desc: 'Spot the changes' },
    slidingPuzzle: { name: 'Sliding Puzzle', desc: 'Slide tiles in order' },
  },
  gameCommon: {
    playAgain: 'Play Again',
    changeDifficulty: 'Change Difficulty',
    backToGames: 'Back to Games',
    newGame: 'New Game',
    tryAgain: 'Try Again',
    keepPlaying: 'Keep Playing',
    congratulations: 'Congratulations!',
    score: 'Score',
    best: 'Best',
    time: 'Time',
    moves: 'Moves',
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Hard',
    gameOver: 'Game Over!',
  },
  memoryGame: {
    chooseDifficulty: 'Choose Difficulty',
    matchPairsSubtitle: 'Match all the pairs to win!',
    encourageAmazing: 'Amazing memory! You are a natural!',
    encourageGreat: 'Great job! Very well done!',
    encourageNice: 'Nice work! Keep practicing!',
    encourageWellDone: 'Well done! Every game makes you sharper!',
  },
  sudokuGame: {
    chooseDifficulty: 'Choose your difficulty:',
    blanks: 'blanks',
    hintsUsed: 'Hints Used',
    hintsDisplay: 'Hints',
    solvedPuzzle: 'You solved the puzzle!',
  },
  game2048: {
    youReached: 'You reached 2048!',
  },
  triviaGame: {
    chooseCategory: 'Choose a category to start your quiz!',
    questions: 'questions',
    questionProgress: 'Question {current} of {total}',
    correct: 'Correct! Well done!',
    incorrect: 'Not quite. The correct answer is highlighted above.',
    nextQuestion: 'Next Question',
    seeResults: 'See Results',
    chooseCategoryBtn: 'Choose Category',
    resultPerfect: 'Perfect score! You are a trivia master!',
    resultExcellent: 'Excellent work! You really know your stuff!',
    resultGreat: 'Great job! You have a wonderful memory!',
    resultGood: 'Good effort! Keep playing to learn more!',
    resultNiceTry: 'Nice try! Every game makes you smarter!',
    history: 'History',
    nature: 'Nature',
    music: 'Music',
    geography: 'Geography',
    science: 'Science',
  },
  wordSearchGame: {
    chooseTheme: 'Choose a theme to start',
    words: 'words',
    theme: 'Theme',
    wordsFound: 'found',
    hintSelectStart: 'Tap the first letter of a word',
    hintSelectEnd: 'Tap the end of the word',
    allWordsFound: 'All Words Found!',
    completionMessage: 'You completed the puzzle!',
    newPuzzle: 'New Puzzle',
    changeTheme: 'Change Theme',
    animals: 'Animals',
    food: 'Food',
    natureTheme: 'Nature',
    home: 'Home',
    colors: 'Colors',
  },
  hangmanGame: {
    guessTheWord: 'Guess the word!',
    category: 'Category',
    youWon: 'You got it!',
    youLost: 'Too many wrong guesses!',
    theWordWas: 'The word was',
    wrongGuesses: 'Wrong guesses',
    animals: 'Animals',
    food: 'Food',
    countries: 'Countries',
    household: 'Household',
  },
  colorMatchGame: {
    watchPattern: 'Watch the pattern...',
    yourTurn: 'Your turn!',
    round: 'Round',
    bestStreak: 'Best streak',
    wrongPattern: 'Wrong! Game over.',
    newRecord: 'New record!',
  },
  solitaireGame: {
    undo: 'Undo',
    draw: 'Draw',
    autoComplete: 'Auto Complete',
    youWon: 'You won!',
    movesUsed: 'Moves used',
  },
  ticTacToeGame: {
    yourTurn: 'Your turn (X)',
    aiTurn: 'Computer is thinking...',
    youWin: 'You win!',
    aiWins: 'Computer wins!',
    draw: "It's a draw!",
    chooseDifficulty: 'Choose difficulty:',
    wins: 'Wins',
    losses: 'Losses',
    draws: 'Draws',
  },
  browser: {
    title: 'Browser',
    noUrl: 'No URL provided',
    emergency: 'Emergency',
  },
  display: {
    title: 'Display & Theme',
    brightness: 'Brightness',
    screen: 'Screen',
    sleepMode: 'Sleep Mode',
    sleepModeDesc: 'Turn off the screen. Tap anywhere to wake up.',
    tapToWake: 'Tap to wake',
    fontSize: 'Font Size',
    fontNormal: 'Normal',
    fontLarge: 'Large',
    fontXLarge: 'X-Large',
    fontHuge: 'Huge',
    fontGiant: 'Giant',
    colorTheme: 'Color Theme',
    light: 'Light',
    dark: 'Dark',
    highContrast: 'High Contrast',
    screensaverMode: 'Screensaver',
    screensaverPhotos: 'Photos',
    screensaverClockOnly: 'Clock Only',
    clockStyle: 'Clock Style',
    clockDigital: 'Digital',
    clockAnalog: 'Analog',
    clockSize: 'Clock Size',
    clockSmall: 'Small',
    clockMedium: 'Medium',
    clockLarge: 'Large',
    weatherLocation: 'Weather Location',
    weatherLocationPlaceholder: 'e.g. Deinze, Belgium',
    weatherLocationHint: 'Leave empty to use building location',
    detectLocation: 'Detect',
    reduceMotion: 'Reduce Motion',
    reduceMotionHint: 'Limit animations and transitions across the app.',
    reduceMotionAuto: 'Auto (follow device)',
    reduceMotionOn: 'On',
    reduceMotionOff: 'Off',
  },
  today: {
    title: 'Today',
    noEvents: 'No events today',
    now: 'NOW',
    allDay: 'All day',
    inTime: 'in {time}',
    nextEvent: 'Next: {title} at {time}',
  },
  weather: {
    title: 'Weather',
    feelsLike: 'Feels like',
    humidity: 'Humidity',
    wind: 'Wind',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    forecast: 'Forecast',
    loading: 'Loading weather...',
    unavailable: 'Weather unavailable',
    high: 'H',
    low: 'L',
  },
  news: {
    title: 'News',
    loading: 'Loading news...',
    noArticles: 'No articles available',
    readMore: 'Read more',
    sources: 'Sources',
    minutesAgo: '{n}m ago',
    hoursAgo: '{n}h ago',
  },
  permissions: {
    title: 'Permissions Required',
    description: 'CARO Care needs the following permissions to work properly. Please allow access to continue.',
    microphone: 'Microphone',
    microphoneDesc: 'Required for phone calls and emergency alarm',
    camera: 'Camera',
    cameraDesc: 'Required for doorbell camera and QR code scanning',
    location: 'Location',
    locationDesc: 'Required for safety monitoring within your building',
    allowAll: 'Allow All Permissions',
    deniedHelp: 'Some permissions were denied. Please open your device Settings > Apps > CARO Care > Permissions to enable them.',
    notifications: 'Notifications',
    notificationsDesc: 'Required for incoming call alerts and emergency notifications',
    openSettings: 'Open Settings',
  },
  battery: {
    lowTitle: 'Low Battery',
    lowMessage: 'Battery is low. Please connect the charger.',
    highTitle: 'Battery Charged',
    highMessage: 'Battery is sufficiently charged. You can disconnect the charger to preserve battery health.',
    lowSpoken: 'Attention. Your battery is low. Please connect the charger.',
  },
};
